import { useSelectedApPayee } from '@/components/ApPayee/useChangeApPayee';
import { useIsActiveInstantPayee } from '@/components/ReportForm/InstantPayeeSwitcher/hooks/useIsActiveInstantPayee';
import { SelectedType } from '@/components/ReportForm/Modals/ChangeXXX';
import { useLazyGetApPayeeDetailWithBookDate } from '@/context/services/ap_payee/ApPayee.service';
import { PaymentRequestForm } from '@/features/InvoiceReport/Edit/type';
import { toISODateString } from 'date-util';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import {
  Control,
  FieldPath,
  FieldValues,
  Path,
  PathValue,
  useFormContext,
  useWatch,
} from 'react-hook-form';

type BookDateByPayeeTypeArgs = {
  control: Control<PaymentRequestForm>;
  originalBookDate: Dayjs | string | undefined;
};

export const useGetBookDateByPayeeType = ({
  control,
  originalBookDate,
}: BookDateByPayeeTypeArgs) => {
  const isActiveInstantPayee = useIsActiveInstantPayee({
    control,
    name: 'payee.value',
  });

  const newBookDateValue = useWatch({ control, name: 'payee.bookDate' });
  const payee = useSelectedApPayee();
  return useMemo(() => {
    if (isActiveInstantPayee) {
      return originalBookDate ? dayjs(originalBookDate) : null;
    }
    return newBookDateValue
      ? dayjs(newBookDateValue)
      : payee?.book_date
      ? dayjs(payee?.book_date)
      : null;
  }, [
    isActiveInstantPayee,
    payee?.book_date,
    newBookDateValue,
    originalBookDate,
  ]);
};

type ChangeBookDateArgs<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  onChangeAfter: ((value: Dayjs | [Dayjs, Dayjs]) => void) | undefined;
};

export const useChangeBookDate = <TFieldValues extends FieldValues>({
  control,
  name,
  onChangeAfter,
}: ChangeBookDateArgs<TFieldValues>) => {
  const selectedPayee = useSelectedApPayee();
  const { resetField } = useFormContext<TFieldValues>();
  const bookDateValue = useWatch({ name, control });
  const fetchApPayeeDetail = useLazyGetApPayeeDetailWithBookDate();
  const [open, setOpen] = useState(false);
  const onWrapChangeAfter = useCallback(
    (value: Dayjs | [Dayjs, Dayjs]) => {
      onChangeAfter?.(value);
      if (selectedPayee?.book_date) {
        // Execute in the next event loop to ensure onWrapChangeAfter completes before
        // updating state, preventing double execution due to re-rendering
        queueMicrotask(() => {
          setOpen(true);
        });
      }
    },
    [onChangeAfter, selectedPayee]
  );
  const onClose = useCallback(() => {
    setOpen(false);
  }, []);
  const onDialogClick = useCallback(
    async (selectedValue: SelectedType) => {
      resetField(name, {
        defaultValue:
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          bookDateValue as PathValue<TFieldValues, Path<TFieldValues>>,
      });
      onClose();
      if (selectedValue === 'all') {
        const data = await fetchApPayeeDetail(
          selectedPayee?.id ?? '',
          toISODateString(bookDateValue)
        );
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        resetField('payee.dueDate' as FieldPath<TFieldValues>, {
          defaultValue:
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            dayjs(data?.payment_date) as PathValue<
              TFieldValues,
              Path<TFieldValues>
            >,
        });
      }
    },
    [
      bookDateValue,
      fetchApPayeeDetail,
      name,
      onClose,
      resetField,
      selectedPayee?.id,
    ]
  );

  return useMemo(
    () => ({
      open,
      onWrapChangeAfter,
      onDialogClick,
      onClose,
    }),
    [onClose, onDialogClick, onWrapChangeAfter, open]
  );
};
